import type React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlayerIdentificationFlow } from './identification/PlayerIdentificationFlow';
import {
  IdentifyPlayersFlow,
  WhoAreYouStep,
} from './identification/IdentifyPlayersFlow';
import { BlockReportCarousel } from './report/BlockReportCarousel';
import { BottomSheetShell } from './report/BottomSheetShell';
import { EliteModal } from './EliteModal';
import type {
  Statistics,
  PlayerSlot,
  SegmentStatistics,
} from '@/types/statistics';
import { NOT_PRESENT } from '@/lib/constants';
import {
  parsePlayersArray,
  computePreIdentifications,
  buildAssociationsMap,
} from '@/lib/transforms';
import {
  identifyOwner,
  previewCorrection,
  putUsernameCorrespondence,
  resetIdentification,
  saveCorrection,
} from '@/lib/api';
import { useEliteStatus } from '@/hooks/useEliteStatus';

/** Per reference player (A..D), their crop in every block of a corrected report. */
const cropsOf = (segments: SegmentStatistics[]) =>
  [1, 2, 3, 4].map((id) => segments.map((s) => s.player_crops[`player${id}`]));

interface ReportPageClientProps {
  data: Statistics;
  sessionId: string;
  externalPlayerId: string;
  /** Set when a signed-in owner opened the report from the Player Dashboard:
      their identification is then saved under `externalPlayerId` (their
      account), even though they aren't a registered player of the session. */
  reportToken?: string | null;
  /** Whether the splash has finished and lifted. Passed through to the report
      so the guided tour doesn't auto-start on top of it. */
  splashDone?: boolean;
  /** Reload the report: after an owner corrects who is who, says who they
      are, or starts over, it's served differently. */
  onReload?: () => void;
}

export const ReportPageClient: React.FC<ReportPageClientProps> = ({
  data,
  sessionId,
  externalPlayerId,
  reportToken = null,
  splashDone = true,
  onReload = () => {},
}) => {
  const { t } = useTranslation('report');
  const { t: tIdentify } = useTranslation('identifyPlayers');
  const [redoOpen, setRedoOpen] = useState(false);
  const viewer = { playerId: externalPlayerId, reportToken };
  // An owner of the match (a booking player of it, or signed in with a report
  // token) must tell who is who and who they are, saved, before seeing their
  // own report. Anyone else the API knows about (owner_report present) goes
  // through the same steps, kept in the page: nothing is saved for them.
  const owner = data.owner_report?.is_owner ? data.owner_report : null;
  const guest = data.owner_report !== undefined && !owner;
  const [guestReport, setGuestReport] = useState<Statistics | null>(null);
  const [guestSlot, setGuestSlot] = useState<PlayerSlot | null>(null);
  // Every block's crop of each player, from who is who, for "which one is
  // you?". Lost if they leave in between: the corrected report's blocks then
  // (merged blocks keep one crop each).
  const [identifiedCrops, setIdentifiedCrops] = useState<string[][] | null>(
    null,
  );
  const { showModal: showEliteModal, dismiss: dismissElite } =
    useEliteStatus(externalPlayerId);
  const [identifications, setIdentifications] = useState<Record<
    number,
    PlayerSlot
  > | null>(null);

  // What the report shows: a guest's own corrected report once they answered,
  // identified in every block (the correction relabelled them all), else the
  // report as loaded.
  const shown = useMemo(
    () =>
      guestReport && guestSlot
        ? {
            ...guestReport,
            players_correspondance: {
              [externalPlayerId]: Object.fromEntries(
                guestReport.segments_statistics.map((s) => [
                  s.metadata.block_id,
                  guestSlot,
                ]),
              ),
            },
          }
        : data,
    [guestReport, guestSlot, data, externalPlayerId],
  );

  // Every block, short ones too: blocks that aren't organic get merged by the
  // who-is-who correction instead of being hidden.
  const segments = shown.segments_statistics;

  // Normalize players array → UUID → avatar URL map
  const avatarMap = useMemo(
    () => parsePlayersArray(shown.players),
    [shown.players],
  );

  // Check if the current user is a known player (exists in raw players data)
  const isKnownPlayer = useMemo(
    () =>
      shown.players.some((entries) =>
        entries.some(
          ([key, value]) => key === 'player_id' && value === externalPlayerId,
        ),
      ),
    [shown.players, externalPlayerId],
  );

  // Compute pre-identifications from existing correspondence
  const userCorrespondence = shown.players_correspondance[externalPlayerId];
  const preIdentifications = useMemo(
    () => computePreIdentifications(userCorrespondence, segments),
    [userCorrespondence, segments],
  );

  // Check if all blocks are pre-identified
  const allPreIdentified = useMemo(
    () =>
      segments.length > 0 &&
      segments.every((_, i) => preIdentifications[i] !== undefined),
    [segments, preIdentifications],
  );

  // Merge new identifications into correspondence so BlockReport resolves avatars immediately
  const mergedCorrespondance = useMemo(() => {
    if (!identifications) return shown.players_correspondance;
    return {
      ...shown.players_correspondance,
      [externalPlayerId]: buildAssociationsMap(identifications, segments),
    };
  }, [
    identifications,
    shown.players_correspondance,
    externalPlayerId,
    segments,
  ]);

  if (segments.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4 text-slate-400">
        {t('noSegments')}
      </div>
    );
  }

  if (owner && !owner.corrected) {
    return (
      <IdentifyPlayersFlow
        sessionId={sessionId}
        viewer={viewer}
        onConfirm={async (mapping, crops) => {
          await saveCorrection(sessionId, viewer, mapping);
          setIdentifiedCrops(crops);
          onReload();
        }}
      />
    );
  }

  if (owner && !owner.identified_as) {
    return (
      <WhoAreYouStep
        crops={identifiedCrops ?? cropsOf(segments)}
        onConfirm={async (slot) => {
          await identifyOwner(sessionId, viewer, slot);
          onReload();
        }}
      />
    );
  }

  if (guest && !guestReport) {
    return (
      <IdentifyPlayersFlow
        sessionId={sessionId}
        viewer={viewer}
        onConfirm={async (mapping, crops) => {
          setGuestReport(await previewCorrection(sessionId, mapping));
          setIdentifiedCrops(crops);
        }}
      />
    );
  }

  if (guest && guestReport && !guestSlot) {
    return (
      <WhoAreYouStep
        crops={identifiedCrops ?? cropsOf(guestReport.segments_statistics)}
        onConfirm={async (slot) => setGuestSlot(slot)}
      />
    );
  }

  if (showEliteModal) {
    return <EliteModal onDismiss={dismissElite} />;
  }

  // Use pre-identifications or manual identifications
  const resolvedIdentifications =
    identifications ?? (allPreIdentified ? preIdentifications : null);

  // Not yet identified → show identification flow (an API without owner
  // reports, or a local fixture)
  if (!resolvedIdentifications) {
    return (
      <div className="flex h-[100dvh] flex-col overflow-hidden p-4">
        <div className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col">
          <PlayerIdentificationFlow
            segments={segments}
            preIdentifications={preIdentifications}
            playersCorrespondance={mergedCorrespondance}
            currentUserUuid={externalPlayerId}
            onComplete={(ids) => {
              setIdentifications(ids);
              // Persist for players registered on the session, or for an owner
              // who came with a report token (saved under their account).
              if (isKnownPlayer || reportToken) {
                putUsernameCorrespondence(
                  sessionId,
                  {
                    player_id: externalPlayerId,
                    associations: buildAssociationsMap(ids, segments),
                  },
                  reportToken,
                ).catch((err) =>
                  console.error('Failed to save correspondence:', err),
                );
              }
            }}
          />
        </div>
      </div>
    );
  }

  // Check if every segment was marked as NOT_PRESENT
  const allSkipped = Object.values(resolvedIdentifications).every(
    (id) => id === NOT_PRESENT,
  );

  if (allSkipped) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4 text-slate-400">
        {t('notIdentified')}
      </div>
    );
  }

  // Starting over: an owner's answers are deleted, a guest's dropped.
  const redo = () => {
    setRedoOpen(false);
    setIdentifiedCrops(null);
    if (owner) {
      resetIdentification(sessionId, viewer)
        .then(onReload)
        .catch((err) => console.error('Failed to reset identification:', err));
    } else {
      setGuestReport(null);
      setGuestSlot(null);
    }
  };

  // Identified → show the multi-block tournament-UI carousel
  return (
    <>
      <BlockReportCarousel
        segments={segments}
        identifications={resolvedIdentifications}
        predictionMode={shown.prediction_mode}
        avatarMap={avatarMap}
        playersCorrespondance={mergedCorrespondance}
        splashDone={splashDone}
        onRedoIdentification={
          owner || guest ? () => setRedoOpen(true) : undefined
        }
      />
      {/* Starting over drops the answers: confirmed first. */}
      <BottomSheetShell open={redoOpen} onClose={() => setRedoOpen(false)}>
        <div className="flex flex-col gap-3 pt-2">
          <h3 className="text-xl font-bold text-slate-900">
            {tIdentify('redoTitle')}
          </h3>
          <p className="text-sm leading-snug text-slate-600">
            {tIdentify('redoBody')}
          </p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              type="button"
              className="min-h-[48px] rounded-xl border border-slate-300 font-medium text-slate-700"
              onClick={() => setRedoOpen(false)}
            >
              {tIdentify('redoCancel')}
            </button>
            <button
              type="button"
              className="min-h-[48px] rounded-xl bg-slate-900 font-medium text-white"
              onClick={redo}
            >
              {tIdentify('redoConfirm')}
            </button>
          </div>
        </div>
      </BottomSheetShell>
    </>
  );
};

export default ReportPageClient;
